// Real-time client-side calculations for Bar Replay
export function calculateReplayData(bars, params = {}) {
  if (!bars || bars.length < 25) {
    return { indicators: null, signals: null };
  }

  const emaFastPeriod = Number(params.matrixEmaFast ?? params.matrix_ema_fast ?? params.emaFast ?? 9);
  const emaSlowPeriod = Number(params.matrixEmaSlow ?? params.matrix_ema_slow ?? params.emaSlow ?? 21);
  const rsiPeriod = Number(params.matrixRsiPeriod ?? params.matrix_rsi_period ?? params.rsiPeriod ?? 14);
  const tpMult = Number(params.matrixTpMult ?? params.matrix_tp_mult ?? params.tpMult ?? 1.8);
  const slMult = Number(params.matrixSlMult ?? params.matrix_sl_mult ?? params.slMult ?? 1.2);
  const minConfidence = Number(params.matrixMinConfidence ?? params.matrix_min_confidence ?? params.minConfidence ?? 35);
  const cooldownBars = Number(params.matrixCooldownBars ?? params.matrix_cooldown_bars ?? params.cooldownBars ?? 12);
  const maxHoldingBars = Number(params.matrixMaxHoldingBars ?? params.maxHoldingBars ?? 30);
  const timeoutExitEnable = params.matrixTimeoutExitEnable !== undefined ? Boolean(params.matrixTimeoutExitEnable) : (params.timeoutExitEnable !== undefined ? Boolean(params.timeoutExitEnable) : false);
  const slFilterEnable = params.matrixSlFilterEnable !== undefined ? Boolean(params.matrixSlFilterEnable) : (params.slFilterEnable !== undefined ? Boolean(params.slFilterEnable) : true);
  const slMin = Number(params.matrixSlMin ?? params.slMin ?? 10);
  const slMax = Number(params.matrixSlMax ?? params.slMax ?? 100);
  const ema200FilterEnable = params.matrixEma200FilterEnable !== undefined ? Boolean(params.matrixEma200FilterEnable) : (params.ema200FilterEnable !== undefined ? Boolean(params.ema200FilterEnable) : true);
  const ema200DistFilterEnable = params.matrixEma200DistFilterEnable !== undefined ? Boolean(params.matrixEma200DistFilterEnable) : Boolean(params.ema200DistFilterEnable);
  const ema200OuterPips = Number(params.matrixEma200OuterPips ?? params.ema200OuterPips ?? 200);
  const ema200InnerPips = Number(params.matrixEma200InnerPips ?? params.ema200InnerPips ?? 100);

  const closes = bars.map((b) => b.close);
  const highs = bars.map((b) => b.high);
  const lows = bars.map((b) => b.low);
  const opens = bars.map((b) => b.open);
  const times = bars.map((b) => b.time);
  const samplePrice = closes[closes.length - 1] || 1.0;
  const pipMult = samplePrice < 5.0 ? 10000 : (samplePrice < 500.0 ? 100 : 10);

  // EMA helper
  const calcEMA = (data, period) => {
    const k = 2 / (period + 1);
    const emaArray = [];
    let prevEma = data[0];
    for (let i = 0; i < data.length; i++) {
      if (i === 0) {
        prevEma = data[0];
      } else {
        prevEma = data[i] * k + prevEma * (1 - k);
      }
      emaArray.push({ time: times[i], value: Math.round(prevEma * 100000) / 100000 });
    }
    return emaArray;
  };

  const ema9 = calcEMA(closes, emaFastPeriod);
  const ema21 = calcEMA(closes, emaSlowPeriod);
  const ema50 = calcEMA(closes, 50);
  const ema200 = calcEMA(closes, 200);

  // RSI calculation
  const rsiArray = [];
  let avgGain = 0;
  let avgLoss = 0;
  for (let i = 1; i <= rsiPeriod; i++) {
    const diff = closes[i] - closes[i - 1];
    if (diff >= 0) avgGain += diff;
    else avgLoss += Math.abs(diff);
  }
  avgGain /= rsiPeriod;
  avgLoss /= rsiPeriod;

  for (let i = 0; i < closes.length; i++) {
    if (i < rsiPeriod) {
      rsiArray.push({ time: times[i], value: 50.0 });
    } else {
      const diff = closes[i] - closes[i - 1];
      const gain = diff > 0 ? diff : 0;
      const loss = diff < 0 ? Math.abs(diff) : 0;
      avgGain = (avgGain * (rsiPeriod - 1) + gain) / rsiPeriod;
      avgLoss = (avgLoss * (rsiPeriod - 1) + loss) / rsiPeriod;
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      const rsiVal = 100 - 100 / (1 + rs);
      rsiArray.push({ time: times[i], value: Math.round(rsiVal * 10) / 10 });
    }
  }

  // ATR calculation
  const atrArray = [];
  let sumTr = 0;
  for (let i = 0; i < closes.length; i++) {
    if (i === 0) {
      atrArray.push(highs[0] - lows[0]);
    } else {
      const tr = Math.max(
        highs[i] - lows[i],
        Math.abs(highs[i] - closes[i - 1]),
        Math.abs(lows[i] - closes[i - 1])
      );
      if (i < 14) {
        sumTr += tr;
        atrArray.push(sumTr / (i + 1));
      } else {
        const prevAtr = atrArray[i - 1];
        const currAtr = (prevAtr * 13 + tr) / 14;
        atrArray.push(currAtr);
      }
    }
  }

  // Bollinger Bands
  const bbUpper = [];
  const bbLower = [];
  for (let i = 0; i < closes.length; i++) {
    if (i < 19) {
      bbUpper.push({ time: times[i], value: closes[i] });
      bbLower.push({ time: times[i], value: closes[i] });
    } else {
      const slice = closes.slice(i - 19, i + 1);
      const mean = slice.reduce((a, b) => a + b, 0) / 20;
      const variance = slice.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / 20;
      const std = Math.sqrt(variance);
      bbUpper.push({ time: times[i], value: Math.round((mean + 2 * std) * 1000) / 1000 });
      bbLower.push({ time: times[i], value: Math.round((mean - 2 * std) * 1000) / 1000 });
    }
  }

  // MACD calculation (12, 26, 9)
  const ema12 = calcEMA(closes, 12);
  const ema26 = calcEMA(closes, 26);
  const macdLine = closes.map((_, idx) => (ema12[idx]?.value ?? 0) - (ema26[idx]?.value ?? 0));
  const signalEma = calcEMA(macdLine, 9);
  const macdHist = macdLine.map((val, idx) => val - (signalEma[idx]?.value ?? 0));

  // Single-Trade & Cooldown Signals Logic
  const chartMarkers = [];
  const historicalSignals = [];
  const activeTrades = [];
  let lastSignalIdx = -999;

  for (let i = Math.max(25, emaSlowPeriod, rsiPeriod); i < closes.length; i++) {
    const currC = closes[i];
    const currH = highs[i];
    const currL = lows[i];
    const currO = opens[i];
    const prevC = closes[i - 1];
    const prevO = opens[i - 1];
    const currAtr = atrArray[i] || currC * 0.003;

    // Track active trades
    for (let k = activeTrades.length - 1; k >= 0; k--) {
      const trade = activeTrades[k];
      const isBuyAction = trade.action === "BUY";
      const tp = trade.tp1;
      const sl = trade.sl;
      let resolved = false;

      if (isBuyAction) {
        if (currH >= tp) {
          trade.status = "WIN_TP";
          trade.pnl_diff = tp - trade.price;
          resolved = true;
        } else if (currL <= sl) {
          trade.status = "LOSS_SL";
          trade.pnl_diff = -(trade.price - sl);
          resolved = true;
        }
      } else {
        if (currL <= tp) {
          trade.status = "WIN_TP";
          trade.pnl_diff = trade.price - tp;
          resolved = true;
        } else if (currH >= sl) {
          trade.status = "LOSS_SL";
          trade.pnl_diff = -(sl - trade.price);
          resolved = true;
        }
      }

      if (timeoutExitEnable && (i - trade.bar_index >= maxHoldingBars) && !resolved) {
        trade.status = "TIMEOUT";
        trade.pnl_diff = isBuyAction ? currC - trade.price : trade.price - currC;
        resolved = true;
      }

      if (resolved) {
        activeTrades.splice(k, 1);
      }
    }

    if (activeTrades.length > 0 || (i - lastSignalIdx) < cooldownBars) continue;

    let score = 0;
    const reasons = [];

    // 1. EMA cross (+/-35)
    if (ema9[i].value > ema21[i].value && ema9[i - 1].value <= ema21[i - 1].value) {
      score += 35;
      reasons.push(`Giao cắt vàng EMA ${emaFastPeriod} / EMA ${emaSlowPeriod} (Golden Cross)`);
    } else if (ema9[i].value < ema21[i].value && ema9[i - 1].value >= ema21[i - 1].value) {
      score -= 35;
      reasons.push(`Giao cắt tử thần EMA ${emaFastPeriod} / EMA ${emaSlowPeriod} (Death Cross)`);
    }

    // 2. Trend Filter (+/-15)
    if (currC > ema50[i].value && ema9[i].value > ema50[i].value) {
      score += 15;
    } else if (currC < ema50[i].value && ema9[i].value < ema50[i].value) {
      score -= 15;
    }

    // 3. RSI Filter (+/-30)
    const currRsi = rsiArray[i].value;
    const prevRsi = rsiArray[i - 1]?.value ?? currRsi;
    if (currRsi < 35 && currRsi > prevRsi) {
      score += 30;
      reasons.push(`RSI(${rsiPeriod}) phục hồi từ Quá Bán (${currRsi})`);
    } else if (currRsi > 65 && currRsi < prevRsi) {
      score -= 30;
      reasons.push(`RSI(${rsiPeriod}) đảo chiều từ Quá Mua (${currRsi})`);
    }

    // 4. MACD Histogram Crossover (+/-20)
    if (macdHist[i] > 0 && macdHist[i - 1] <= 0) {
      score += 20;
      reasons.push("MACD Histogram chuyển sang Dương");
    } else if (macdHist[i] < 0 && macdHist[i - 1] >= 0) {
      score -= 20;
      reasons.push("MACD Histogram chuyển sang Âm");
    }

    // 5. Candlestick Engulfing Pattern (+/-25)
    const isBullEngulfing = (prevC < prevO) && (currC > currO) && (currC > prevO) && (currO < prevC);
    const isBearEngulfing = (prevC > prevO) && (currC < currO) && (currC < prevO) && (currO > prevC);
    if (isBullEngulfing) {
      score += 25;
      reasons.push("Mẫu hình nến Nhấn chìm Tăng (Bullish Engulfing)");
    } else if (isBearEngulfing) {
      score -= 25;
      reasons.push("Mẫu hình nến Nhấn chìm Giảm (Bearish Engulfing)");
    }

    let signalType = null;
    let action = null;
    const confidence = Math.min(Math.abs(score), 98);

    if (score >= minConfidence) {
      signalType = score >= 75 ? "STRONG_BUY" : "BUY";
      action = "BUY";
    } else if (score <= -minConfidence) {
      signalType = score <= -75 ? "STRONG_SELL" : "SELL";
      action = "SELL";
    }

    if (signalType) {
      if (ema200FilterEnable && ema200[i]) {
        const e200Val = ema200[i].value;
        if (action === "BUY" && currC < e200Val) signalType = null;
        else if (action === "SELL" && currC > e200Val) signalType = null;
      }
      if (signalType && ema200DistFilterEnable && ema200[i]) {
        const e200Val = ema200[i].value;
        const distPips = Math.abs(currC - e200Val) * pipMult;
        if (distPips > ema200OuterPips || distPips < ema200InnerPips) signalType = null;
      }
      if (signalType && slFilterEnable) {
        const slDistPips = (slMult * currAtr) * pipMult;
        if (slDistPips < slMin || slDistPips > slMax) signalType = null;
      }
    }

    if (signalType) {
      const digits = currC < 5 ? 5 : (currC < 500 ? 3 : 2);
      const sl = action === "BUY" ? Math.round((currC - slMult * currAtr) * 100) / 100 : Math.round((currC + slMult * currAtr) * 100) / 100;
      const tp1 = action === "BUY" ? Math.round((currC + tpMult * currAtr) * 100) / 100 : Math.round((currC - tpMult * currAtr) * 100) / 100;
      const tp2 = action === "BUY" ? Math.round((currC + tpMult * 1.6 * currAtr) * 100) / 100 : Math.round((currC - tpMult * 1.6 * currAtr) * 100) / 100;

      const marker = {
        time: times[i],
        position: action === "BUY" ? "belowBar" : "aboveBar",
        color: action === "BUY" ? (signalType === "STRONG_BUY" ? "#10b981" : "#22c55e") : (signalType === "STRONG_SELL" ? "#ef4444" : "#f87171"),
        shape: action === "BUY" ? "arrowUp" : "arrowDown",
        text: `${signalType === "STRONG_BUY" ? "STRONG " : ""}${action} (${confidence}%)`,
      };

      const sigObj = {
        id: `sig_${times[i]}`,
        time: times[i],
        bar_index: i,
        action,
        type: signalType,
        price: currC,
        sl,
        tp1,
        tp2,
        rr_ratio: `1:${Math.round((tpMult / slMult) * 10) / 10}`,
        score,
        confidence,
        reasons,
        status: "RUNNING",
        pnl_diff: 0,
        entry_type: "Candle Close (Non-Repaint)",
      };

      activeTrades.push(sigObj);
      lastSignalIdx = i;
      historicalSignals.push(sigObj);
      chartMarkers.push(marker);
    }
  }

  const nowTs = times[times.length - 1] || Math.floor(Date.now() / 1000);
  const oneDay = 24 * 3600;
  const windowsConfig = {
    today: nowTs - oneDay,
    "24h": nowTs - oneDay,
    "3d": nowTs - 3 * oneDay,
    "7d": nowTs - 7 * oneDay,
    "30d": nowTs - 30 * oneDay,
    all: null
  };

  const backtestWindows = {};
  for (const [winKey, startTsLimit] of Object.entries(windowsConfig)) {
    const minTime = startTsLimit !== null ? startTsLimit : 0;
    const winTrades = historicalSignals.filter(s => s.time >= minTime && ["WIN_TP", "LOSS_SL", "TIMEOUT"].includes(s.status));
    const winsCount = winTrades.filter(s => (s.pnl_diff || 0) >= 0).length;
    const lossesCount = winTrades.filter(s => (s.pnl_diff || 0) < 0).length;
    const totalCount = winsCount + lossesCount;

    let grossP = 0;
    let grossL = 0;
    winTrades.forEach(s => {
      const diff = s.pnl_diff || 0;
      if (diff > 0) grossP += diff;
      else if (diff < 0) grossL += Math.abs(diff);
    });
    const netDiff = grossP - grossL;

    const winRate = totalCount > 0 ? Math.round((winsCount / totalCount) * 1000) / 10 : 0.0;
    const profitFactor = grossL > 0 ? Math.round((grossP / grossL) * 100) / 100 : (grossP > 0 ? 2.5 : 1.0);
    const netPips = Math.round(netDiff * pipMult * 10) / 10;

    backtestWindows[winKey] = {
      total_trades: totalCount,
      win_rate: winRate,
      wins: winsCount,
      losses: lossesCount,
      net_pnl: netPips,
      profit_factor: profitFactor,
      avg_rr: `1:${Math.round((tpMult / slMult) * 10) / 10}`
    };
  }

  const lastBarIdx = closes.length - 1;
  const indicators = {
    ema9,
    ema21,
    ema50,
    ema200,
    rsi: rsiArray,
    bollinger: { upper: bbUpper, lower: bbLower },
    latest: {
      ema9: ema9[lastBarIdx]?.value,
      ema21: ema21[lastBarIdx]?.value,
      ema50: ema50[lastBarIdx]?.value,
      ema200: ema200[lastBarIdx]?.value,
      rsi: rsiArray[lastBarIdx]?.value,
    },
  };

  const latestC = closes[lastBarIdx] || 0;
  let overallScore = 0;
  if (latestC > (ema21[lastBarIdx]?.value || 0)) overallScore += 25;
  else overallScore -= 25;
  if ((rsiArray[lastBarIdx]?.value || 50) > 50) overallScore += 25;
  else overallScore -= 25;
  if ((macdHist[lastBarIdx] || 0) > 0) overallScore += 25;
  else overallScore -= 25;
  if ((ema9[lastBarIdx]?.value || 0) > (ema50[lastBarIdx]?.value || 0)) overallScore += 25;
  else overallScore -= 25;

  let summarySentiment = "NEUTRAL";
  if (overallScore >= 50) summarySentiment = "STRONG_BULLISH";
  else if (overallScore > 0) summarySentiment = "BULLISH";
  else if (overallScore <= -50) summarySentiment = "STRONG_BEARISH";
  else if (overallScore < 0) summarySentiment = "BEARISH";

  const signals = {
    latest_signal: historicalSignals[historicalSignals.length - 1] || null,
    recent_signals: historicalSignals.slice(-15),
    all_signals: historicalSignals,
    chart_markers: chartMarkers.slice(-50),
    sentiment_score: overallScore,
    market_bias: summarySentiment,
    backtest: backtestWindows["all"],
    backtest_windows: backtestWindows,
  };

  return { indicators, signals };
}